import BigButton from "components/atoms/buttons/BigButton";
import TableButton from "components/atoms/buttons/TableButton";
import {
  CenterlineText,
  Text,
  UnderlineText,
} from "components/atoms/text/Text";
import PaginationComponent from "components/blocks/pagination";
import { COLORS, LIGHT_THEME_COLORS } from "styles/colors";
import * as s from "./AboutStyled";
import * as mediaQuery from "components/MediaQuery";
import {
  BasePagination,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { getLicenseList, getVersion } from "api/helpAPI";
import {
  GetLicenseListResponse,
  GetVersionResponse,
  LicenseItem,
} from "api/interfaces/helpInterface.interface";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import TermOfCondition from "./TermOfCondition";
import { IssueReport } from "./IssueReport";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { MYDW_LOGO } from "components/blocks/sidenav/TopInfo";
import Announcement from "components/blocks/announcement/Announcement";
import { useAWS } from "hook/useAWS";
import ReleaseNote from "pages/recorders/edit/updated/modal/ReleaseNote";
import ReleaseModal from "./releaseNotes/ReleaseModal";
import { GrBug, GrDocumentText } from "react-icons/gr";

type Props = {};

export default function About(props: Props): JSX.Element {
  const [version, setVersion] = useState<string>("");
  const [licenseData, setLicenseData] = useState<LicenseItem[]>([]);

  const account: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const [licenseQueryInfo, setLicenseQueryInfo] = useState<BasePagination>({
    total: 0,
    pageNum: 0,
    pageLimit: 10,
    keyword: "",
    sortType: "name",
    sortDirection: SORT_DIRECTION.ASC,
  });

  const versionQuery = useQuery(["getVersion"], () => getVersion(), {
    retry: 0,
    refetchOnWindowFocus: false,
    onSuccess: (res: GetVersionResponse) => {
      if (res.error !== 0 || res.result === undefined) {
        setVersion("");
        return;
      }
      setVersion(res.result);
    },
    onError: (e: any) => {
      setVersion("");
    },
  });

  const { data, error, refetch } = useQuery(
    ["licenseList", licenseQueryInfo],
    () =>
      getLicenseList({
        payload: licenseQueryInfo,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: GetLicenseListResponse) => {
        if (res.error !== 0 || res.result === undefined) {
          setLicenseData([]);
          setLicenseQueryInfo((info) => {
            return { ...info, total: 0 as number, pageNum: 0 };
          });
          return;
        }
        setLicenseData(res.result);
        setLicenseQueryInfo((info) => {
          return { ...info, total: res.page.total as number };
        });
      },
      onError: (e: any) => {
        setLicenseData([]);
        setLicenseQueryInfo((info) => {
          return { ...info, total: 0 as number, pageNum: 0 };
        });
      },
    }
  );

  const onChangePage = useCallback(
    (page: number, totalRows: number) => {
      if (page !== licenseQueryInfo.pageNum + 1) {
        setLicenseQueryInfo((info) => {
          return { ...info, pageNum: (page - 1) as number };
        });
      }
    },
    [licenseQueryInfo.pageNum]
  );

  const [visibleTC, setVisibleTC] = useState<boolean>(false);
  const [issue, setIssue] = useState<boolean>(false);
  const { docScan } = useAWS();

  function onModelIssue(): void {
    setIssue(false);
  }
  const [releaseNotesList, setReleaseNotesList] = useState<any[]>([]);
  const [isShowReleaseNotes, setIsShowReleaseNotes] = useState<boolean>(false);
  useEffect(() => {
    try {
      docScan(process.env.REACT_APP_RELEASE_NOTES_TABLE_NAME as string)
        .then((res: any) => {
          setReleaseNotesList(
            res.Items.filter((item: any) => item.isActive).sort(
              (a: any, b: any) =>
                new Date(b.releasedAt).getTime() -
                new Date(a.releasedAt).getTime()
            )
          );
        })
        .catch((error: any) => {
          console.error(error);
        });
    } catch (error) {
      console.error(error);
    }
  }, []);
  const theme = useAppSelector((state) => state.theme.theme);
  const isLight = theme === "light";

  return (
    <>
      <s.Container>
        <s.Section>
          <s.Inner>
            <s.Title>About myDW</s.Title>

            <s.Info>
              <img src={MYDW_LOGO} alt={"about_dw_logo"} />
              <s.VersionInfo>
                <p>Version {process.env.REACT_APP_VERSION}</p>
                <Announcement isShowButton={true} />
                {releaseNotesList.length > 0 && (
                  <button
                    onClick={() => setIsShowReleaseNotes(true)}
                    data-testid="help-about-help-button-set-is-show-release-notes"
                  >
                    <GrDocumentText />
                    Release Notes
                  </button>
                )}

                {/* {account.accountLevel !== LEVEL_TYPE.DW && (
                   <button onClick={() => setIssue(true)}>
                     <GrBug />
                     Report An Issue
                   </button>
                  )} */}
              </s.VersionInfo>
            </s.Info>
            <div>
              <CenterlineText center color={COLORS.BUTTONS1} fontSize={20}>
                myDW
              </CenterlineText>
            </div>
            <s.Copyright>
              Copyright 2025 Digital Watchdog Inc. All rights reserved.
              <br />
              <br />
              myDW is made possible by the contributions of various open source
              software developers.
              <br />
              <br />
              <button
                onClick={() => window.open("/terms/eula", "_blank")}
                data-testid="help-about-help-button-open"
              >
                <Text color={COLORS.BUTTONS2} fontSize={15}>
                  End User License Agreement
                </Text>
              </button>
            </s.Copyright>
          </s.Inner>
        </s.Section>
        <s.Section>
          <s.Inner>
            <s.Title>Open Source Software Used</s.Title>
            <s.ListWrapper>
              {licenseData.map(
                (item, index) =>
                  index <= 9 && (
                    <s.ListItem key={index}>
                      <Text>{item.name}</Text>
                      <Text>{item.version}</Text>
                      <Text>{item.moduleLicense}</Text>
                      <s.ButtonWrapper>
                        <mediaQuery.Default>
                          {/* <TableButton
                        label="Show License"
                        onClick={() => console.log("show license")}
                        /> */}
                          <TableButton
                            label="Homepage"
                            onClick={() =>
                              item.moduleUrl !== undefined
                                ? window.open(
                                    item.moduleUrl.replaceAll("git+", ""),
                                    "_blank"
                                  )
                                : ""
                            }
                            data-testid="help-about-help-table-button-open"
                          />
                        </mediaQuery.Default>
                        <mediaQuery.Mobile>
                          {/* <UnderlineText color={COLORS.SELECTED}>
                        Show License
                        </UnderlineText> */}
                          <UnderlineText
                            onClick={() =>
                              item.moduleUrl !== undefined
                                ? window.open(
                                    item.moduleUrl.replaceAll("git+", ""),
                                    "_blank"
                                  )
                                : ""
                            }
                            data-testid="help-about-help-underline-text-open"
                          >
                            Homepage
                          </UnderlineText>
                        </mediaQuery.Mobile>
                      </s.ButtonWrapper>
                    </s.ListItem>
                  )
              )}
            </s.ListWrapper>
            <s.PageWrapper>
              <PaginationComponent
                rowsPerPage={licenseQueryInfo.pageLimit}
                rowCount={licenseQueryInfo.total}
                currentPage={licenseQueryInfo.pageNum}
                onChangePage={onChangePage}
              />
            </s.PageWrapper>
          </s.Inner>
        </s.Section>
      </s.Container>
      {isShowReleaseNotes && (
        <ReleaseModal
          onClickCancel={() => setIsShowReleaseNotes(false)}
          releaseNotesList={releaseNotesList}
        />
      )}

      {issue && <IssueReport onModal={onModelIssue} />}
      {visibleTC && (
        <BigPopupTemplate
          title="Term of Service"
          onModal={() => setVisibleTC(false)}
        >
          <TermOfCondition />
        </BigPopupTemplate>
      )}
    </>
  );
}
